import { Observable } from 'relay-runtime';
import type { Sink } from 'relay-runtime/lib/network/RelayObservable';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { processStream } from './processStream';
import type { GraphQLMultipartResponsePayload } from './types';

const NO_CONTENT = 204;

function getBoundary(contentType: string): string | null {
	const parts = contentType.split('boundary=');
	if (parts.length < 2) {
		return null;
	}
	return parts[1].trim();
}

/**
 * Optional product-provided hook that gets a chance to transform / decorate /
 * replace any error before it is surfaced to relay. See
 * `ExternalDependencies['onFetchError']` in
 * `platform/packages/relay/relay-fetch/src/multi-fetch.ts` for the full
 * contract. `response` is `undefined` when the failure occurred before an
 * HTTP `Response` was received.
 */
type OnFetchError = (error: unknown, response?: Response) => unknown;

/**
 * Forward `error` to `observer.error`, giving the product-provided
 * `onFetchError` (if any) a chance to transform it first.
 */
function emitError(
	observer: Sink<GraphQLMultipartResponsePayload>,
	error: unknown,
	response: Response | undefined,
	onFetchError: OnFetchError | undefined,
): void {
	const transformed = onFetchError ? onFetchError(error, response) : error;
	// `Sink.error` accepts `Error` per its type, but in practice relay
	// forwards whatever it receives to consumers. Cast to keep the existing
	// signature while allowing products to return any error-like value.
	observer.error(transformed as Error);
}

function handleMultipartResponse(
	response: Response,
	observer: Sink<GraphQLMultipartResponsePayload>,
	onFetchError: OnFetchError | undefined,
): void {
	const contentType = response.headers.get('content-type');
	const boundary = getBoundary(contentType || '');

	if (!boundary) {
		emitError(observer, new Error('Invalid multipart boundary'), response, onFetchError);
		return;
	}

	const decoder = new TextDecoder('utf-8');

	async function* streamReader() {
		// @ts-expect-error a Response object in Node is streamable
		for await (const chunk of response.body) {
			const newText = decoder.decode(chunk, { stream: true });
			yield newText;
		}
	}

	try {
		// Depending on the version of Node we're running in, the Response object may not implement ReadableStream.
		// Here, we treat the response as an async iterator, since both node-fetch and browser implement the response body as a stream.
		// We wrap that iteration in an async generator, which lets us
		processStream(streamReader(), boundary, observer).catch((error) =>
			emitError(observer, error, response, onFetchError),
		);
	} catch (e) {
		const error = new Error('Failed to read from response body');
		// @ts-ignore: cause property may not be available in all environments
		error.cause = e;
		emitError(observer, error, response, onFetchError);
	}
}

function handleJsonResponse(
	response: Response,
	observer: Sink<GraphQLMultipartResponsePayload>,
	onFetchError: OnFetchError | undefined,
): void {
	response
		.json()
		.then((data) => {
			return observer.next(data);
		})
		.catch((error) => emitError(observer, error, response, onFetchError))
		.finally(() => observer.complete());
}

function handleTextResponse(
	response: Response,
	observer: Sink<GraphQLMultipartResponsePayload>,
	onFetchError: OnFetchError | undefined,
): void {
	response
		.text()
		.then((data) => {
			const maybeJson = data ? JSON.parse(data) : null;
			observer.next(maybeJson);
			observer.complete();
		})
		.catch((responseError) => {
			let traceId = '';
			if (fg('platform_relay_use_shared_relay_env_help_center')) {
				traceId = `(trace-id: ${response.headers.get('atl-traceid')}) `;
			}
			const error = new Error(
				`Error while parsing text response ${traceId}(Content-Type: ${response?.headers?.get(
					'content-type',
				)}): ${responseError instanceof Error ? responseError.message : String(responseError)}`,
			);

			emitError(observer, error, response, onFetchError);
		});
}

export function processMultipartPromise(
	source: () => Promise<Response>,
	onFetchError?: OnFetchError,
	onResponseHeaders?: (headers: Headers) => void,
): Observable<GraphQLMultipartResponsePayload> {
	// Cache the result of the source promise to avoid calling it multiple times.
	const result = source();
	// Wrap an observer around the result to handle the response.
	return Observable.create<GraphQLMultipartResponsePayload>((observer) => {
		result
			.then((response) => {
				// Expose the response headers via a side-channel so the consumer can
				// read values such as `atl-traceid` without changing the payload emission shape.
				if (fg('platform_relay_fetch_traceid_header_fallback')) {
					onResponseHeaders?.(response.headers);
				}

				if (response.status === NO_CONTENT) {
					observer.complete();
					return;
				}

				const contentType = response.headers.get('content-type') || '';

				if (
					contentType.includes('application/json') ||
					contentType.includes('application/graphql-response+json')
				) {
					handleJsonResponse(response, observer, onFetchError);
				} else if (contentType.includes('text/plain')) {
					handleTextResponse(response, observer, onFetchError);
				} else if (contentType.includes('multipart/mixed')) {
					handleMultipartResponse(response, observer, onFetchError);
				} else {
					handleTextResponse(response, observer, onFetchError);
				}
			})
			// Pre-response failure (network error, AbortController, etc.) — no
			// Response object is in scope, so pass `undefined`.
			.catch((error) => emitError(observer, error, undefined, onFetchError));
	});
}
